/**
 * The JQL translator for the scope-filter builder (#1867 PR2; moved out of
 * `lib/scopeFilter.ts`).
 *
 * Owns both directions between the shared builder model and a JQL filter
 * fragment, so the builder and the Advanced (raw JQL) view can never drift.
 * It produces / consumes the FILTER FRAGMENT ONLY — the backend `buildJql`
 * owns the `project` / `issuetype` / `updated` clauses and the `ORDER BY`,
 * and wraps this fragment in parens. So compile MUST NOT emit any of those,
 * and parse only ever sees the fragment. See `../scopeFilter` for the
 * contract every translator keeps.
 *
 * Registry-based so a field is added by extending `FIELD_CONFIGS` + `FIELDS`,
 * not by rewriting the compiler. Its own file so another language's
 * translator is added beside it without touching this one.
 */
import type {
  BuilderModel,
  Condition,
  FieldConfig,
  OperatorKey,
  ScopeFilterModel,
  ScopeFilterTranslator,
} from '../scopeFilter'

/** The fields this translator supports. */
export type JqlFieldKey =
  | 'statusCategory'
  | 'fixVersion'
  | 'priority'
  | 'labels'
  | 'assignee'
  | 'reporter'
  | 'created'
  | 'updated'

interface JqlFieldConfig extends FieldConfig {
  key: JqlFieldKey
  jqlField: string
}

const SET_OPS: OperatorKey[] = ['isAnyOf', 'isNotAnyOf', 'isEmpty']
const DATE_OPS: OperatorKey[] = ['inLast', 'before', 'after']

/** The fixed workflow-stage values — Jira's status categories, no discovery. */
export const STATUS_CATEGORY_VALUES = ['To Do', 'In Progress', 'Done'] as const

const FIELD_CONFIGS: Record<JqlFieldKey, JqlFieldConfig> = {
  statusCategory: { key: 'statusCategory', jqlField: 'statusCategory', label: 'Workflow stage', valueKind: 'fixed', operators: SET_OPS, options: STATUS_CATEGORY_VALUES },
  fixVersion: { key: 'fixVersion', jqlField: 'fixVersion', label: 'Fix version', valueKind: 'versions', operators: SET_OPS },
  priority: { key: 'priority', jqlField: 'priority', label: 'Priority', valueKind: 'priorities', operators: SET_OPS },
  labels: { key: 'labels', jqlField: 'labels', label: 'Labels', valueKind: 'labels', operators: SET_OPS },
  assignee: { key: 'assignee', jqlField: 'assignee', label: 'Assignee', valueKind: 'user', operators: SET_OPS },
  reporter: { key: 'reporter', jqlField: 'reporter', label: 'Reporter', valueKind: 'user', operators: SET_OPS },
  created: { key: 'created', jqlField: 'created', label: 'Created', valueKind: 'date', operators: DATE_OPS },
  updated: { key: 'updated', jqlField: 'updated', label: 'Updated', valueKind: 'date', operators: DATE_OPS },
}

/** Field list for the builder's field dropdown (ordered). */
export const FIELDS: ReadonlyArray<JqlFieldConfig> = [
  FIELD_CONFIGS.statusCategory,
  FIELD_CONFIGS.labels,
  FIELD_CONFIGS.fixVersion,
  FIELD_CONFIGS.priority,
  FIELD_CONFIGS.assignee,
  FIELD_CONFIGS.reporter,
  FIELD_CONFIGS.created,
  FIELD_CONFIGS.updated,
]

/** jqlField (lowercased) → field key, for parse. */
const JQL_FIELD_TO_KEY: Record<string, JqlFieldKey> = Object.fromEntries(
  Object.values(FIELD_CONFIGS).map((c) => [c.jqlField.toLowerCase(), c.key]),
) as Record<string, JqlFieldKey>

function configFor(field: string): JqlFieldConfig | undefined {
  return FIELD_CONFIGS[field as JqlFieldKey]
}

// ── compile ────────────────────────────────────────────────────────────────

function quoteValue(v: string): string {
  return '"' + v.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"'
}

/** A single relative-date token the builder emits, e.g. `-30d` / `-2w`. */
const RELATIVE_TOKEN = /^-\d+[dw]$/

/** Compile one condition to a JQL clause, or null when incomplete/invalid
 * (skipped so a live preview still works mid-edit). */
function compileCondition(c: Condition): string | null {
  const cfg = configFor(c.field)
  if (!cfg) return null
  const f = cfg.jqlField
  switch (c.op) {
    case 'isEmpty':
      return `${f} is EMPTY`
    case 'isAnyOf':
    case 'isNotAnyOf': {
      const vals = c.values.map((v) => v.trim()).filter((v) => v.length > 0)
      if (vals.length === 0) return null
      return `${f} ${c.op === 'isNotAnyOf' ? 'not in' : 'in'} (${vals.map(quoteValue).join(', ')})`
    }
    case 'inLast': {
      // Relative token, emitted UNQUOTED (Jira's relative dates aren't quoted).
      const token = (c.values[0] ?? '').trim()
      return RELATIVE_TOKEN.test(token) ? `${f} >= ${token}` : null
    }
    case 'before':
    case 'after': {
      const date = (c.values[0] ?? '').trim()
      return date ? `${f} ${c.op === 'before' ? '<' : '>'} ${quoteValue(date)}` : null
    }
    case 'is':
      // No JQL field offers it; another language's operator.
      return null
  }
}

/**
 * Compile a model to the JQL filter fragment (no project/type/updated, no
 * ORDER BY). Advanced mode returns its raw JQL untouched. Builder mode joins
 * complete conditions with AND (match all) or OR (match any); an incomplete
 * condition (an operator that needs values but has none) is skipped, so a
 * live preview still works while the operator is mid-edit (the card blocks
 * Save separately via `isConditionComplete`).
 */
export function compile(model: ScopeFilterModel): string {
  if (model.mode === 'advanced') return model.text.trim()
  const clauses = model.conditions.map(compileCondition).filter((c): c is string => c !== null)
  if (clauses.length === 0) return ''
  return clauses.join(model.match === 'any' ? ' OR ' : ' AND ')
}

// ── parse ────────────────────────────────────────────────────────────────

/** Split on top-level ` AND ` / ` OR ` (respecting quotes + parens). Returns
 * the single joiner used (or `null` for one clause), or `'mixed'` when both
 * appear at the top level — which the flat builder can't represent. */
function splitTopLevel(
  s: string,
): { joiner: 'AND' | 'OR' | null; parts: string[] } | 'mixed' {
  const parts: string[] = []
  let depth = 0
  let inQuote = false
  let cur = ''
  let joiner: 'AND' | 'OR' | null = null
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (inQuote) {
      if (ch === '\\' && i + 1 < s.length) {
        cur += ch + s[i + 1]
        i++
        continue
      }
      cur += ch
      if (ch === '"') inQuote = false
      continue
    }
    if (ch === '"') {
      inQuote = true
      cur += ch
      continue
    }
    if (ch === '(') {
      depth++
      cur += ch
      continue
    }
    if (ch === ')') {
      depth--
      cur += ch
      continue
    }
    if (depth === 0) {
      const rest = s.slice(i)
      const andM = /^\s+AND\s+/i.exec(rest)
      const orM = /^\s+OR\s+/i.exec(rest)
      if (andM) {
        if (joiner === 'OR') return 'mixed'
        joiner = 'AND'
        parts.push(cur)
        cur = ''
        i += andM[0].length - 1
        continue
      }
      if (orM) {
        if (joiner === 'AND') return 'mixed'
        joiner = 'OR'
        parts.push(cur)
        cur = ''
        i += orM[0].length - 1
        continue
      }
    }
    cur += ch
  }
  if (inQuote || depth !== 0) return 'mixed' // malformed → bail to Advanced
  parts.push(cur)
  return { joiner, parts }
}

/** Split a `(...)` value list on top-level commas, unquoting. Accepts quoted
 * (compile's output) and bare single-word values (lenient hand-edits). */
function parseValueList(inner: string): string[] | null {
  const out: string[] = []
  let cur = ''
  let inQuote = false
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i]
    if (inQuote) {
      if (ch === '\\' && i + 1 < inner.length) {
        cur += inner[i + 1]
        i++
        continue
      }
      if (ch === '"') {
        inQuote = false
        continue
      }
      cur += ch
      continue
    }
    if (ch === '"') {
      inQuote = true
      continue
    }
    if (ch === ',') {
      out.push(cur.trim())
      cur = ''
      continue
    }
    cur += ch
  }
  if (inQuote) return null
  out.push(cur.trim())
  const vals = out.map((v) => v.trim()).filter((v) => v.length > 0)
  return vals.length > 0 ? vals : null
}

/** Recognize one clause. Two grammars: the SET grammar (`field in (...)` /
 * `not in` / `is EMPTY`) for string-set fields, and the COMPARISON grammar
 * (`field >= -Nd` / `< "date"` / `> "date"`) for date fields. Returns null
 * (→ the whole filter falls to Advanced) for anything else. */
function parseClause(clause: string): Condition | null {
  // Set grammar — string-set fields only (date fields use comparison below).
  const setM = /^\s*([A-Za-z]+)\s+(not\s+in|in|is)\s+(.+?)\s*$/i.exec(clause)
  if (setM) {
    const key = JQL_FIELD_TO_KEY[setM[1].toLowerCase()]
    if (!key || FIELD_CONFIGS[key].valueKind === 'date') return null
    const op = setM[2].toLowerCase().replace(/\s+/g, ' ')
    const rhs = setM[3].trim()
    if (op === 'is') {
      return /^empty$/i.test(rhs) ? { field: key, op: 'isEmpty', values: [] } : null
    }
    const listM = /^\(\s*([\s\S]*?)\s*\)$/.exec(rhs)
    if (!listM) return null
    const values = parseValueList(listM[1])
    if (!values) return null
    return { field: key, op: op === 'not in' ? 'isNotAnyOf' : 'isAnyOf', values }
  }
  // Comparison grammar — date fields only.
  const cmpM = /^\s*([A-Za-z]+)\s+(>=|<|>)\s+(.+?)\s*$/.exec(clause)
  if (cmpM) {
    const key = JQL_FIELD_TO_KEY[cmpM[1].toLowerCase()]
    if (!key || FIELD_CONFIGS[key].valueKind !== 'date') return null
    const sym = cmpM[2]
    const rhs = cmpM[3].trim()
    if (sym === '>=') {
      // Only the relative "in the last N" shape round-trips; e.g.
      // `>= startOfMonth(-1)` can't be shown as a rule → Advanced.
      return RELATIVE_TOKEN.test(rhs) ? { field: key, op: 'inLast', values: [rhs] } : null
    }
    const dm = /^"([^"]*)"$/.exec(rhs)
    const date = dm ? dm[1] : rhs
    return date ? { field: key, op: sym === '<' ? 'before' : 'after', values: [date] } : null
  }
  return null
}

/**
 * Parse a stored JQL scope filter into the builder model, or fall back to
 * Advanced (raw JQL, byte-preserved) for anything the flat builder can't
 * represent. Empty ⇒ an empty builder (today's all-mapped-types scope).
 */
export function parse(scopeFilter: string | undefined | null): ScopeFilterModel {
  const text = (scopeFilter ?? '').trim()
  if (text === '') return { mode: 'builder', match: 'all', conditions: [] }
  if (/\border\s+by\b/i.test(text)) return { mode: 'advanced', text }
  const split = splitTopLevel(text)
  if (split === 'mixed') return { mode: 'advanced', text }
  const conditions: Condition[] = []
  for (const part of split.parts) {
    const c = parseClause(part)
    if (!c) return { mode: 'advanced', text }
    conditions.push(c)
  }
  const model: BuilderModel = { mode: 'builder', match: split.joiner === 'OR' ? 'any' : 'all', conditions }
  return model
}

export const jqlTranslator: ScopeFilterTranslator = {
  languageName: 'JQL',
  fields: FIELDS,
  compile,
  parse,
}
